/* eslint-disable no-restricted-syntax */
/* eslint-disable no-await-in-loop */
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import lightStyles from './portalStylesLight.module.css';
import darkStyles from './portalStylesDark.module.css';
import Notes from './Notes';

interface Props {
  token: string;
  lightMode: boolean;
  superAdmin: boolean;
  orgId: string;
}

interface User {
  id: string;
  name: string;
  isAdmin: boolean;
  threshold?: string;
  schedule?: number[] | null;
}

const MAX_THRESHOLD = 50;

// Convert schedule hour/minute between UTC and local time
function convertScheduleWithOffset(schedule: number[] | null, direction: 'toLocal' | 'toUTC'): number[] | null {
  if (!Array.isArray(schedule)) return null;

  const offsetMinutes = new Date().getTimezoneOffset();
  const offsetHours = Math.floor(offsetMinutes / 60);
  const offsetRemainder = offsetMinutes % 60;

  let hour = schedule[0];
  let minute = schedule[1];

  if (direction === 'toLocal') {
    minute -= offsetRemainder;
    hour -= offsetHours;
  } else {
    minute += offsetRemainder;
    hour += offsetHours;
  }

  // Handle minute overflow/underflow
  if (minute >= 60) {
    minute -= 60;
    hour += 1;
  } else if (minute < 0) {
    minute += 60;
    hour -= 1;
  }

  // Wrap around 24h
  hour = ((hour % 24) + 24) % 24;

  return [hour, minute];
}

export default function Users({ token, lightMode, superAdmin, orgId }: Props) {
  const [rowData, setRowData] = useState<User[]>([]);
  const gridRef = useRef<AgGridReact>(null);
  const styles = lightMode ? lightStyles : darkStyles;

  useEffect(() => {
    (async () => {
      try {
        const [userList, roles, schedules]: [any[], string[], { userId: string; hour: string; minute: string }[]] =
          await Promise.all([
            fetch(`${process.env.REACT_APP_API_URL}/user-list`, {
              method: 'GET',
              headers: {
                authorization: token,
                'Content-Type': 'application/json',
              },
            }).then((res) => res.json()),

            fetch(`${process.env.REACT_APP_API_URL}/roles`, {
              method: 'GET',
              headers: {
                authorization: token,
                'Content-Type': 'application/json',
              },
            }).then((res) => res.json()),

            fetch(`${process.env.REACT_APP_API_URL}/logoff/schedule`, {
              method: 'GET',
              headers: {
                authorization: token,
                'Content-Type': 'application/json',
              },
            }).then((res) => res.json()),
          ]);

        const scheduleMap = new Map(
          schedules.map((s) => [s.userId, convertScheduleWithOffset([Number(s.hour), Number(s.minute)], 'toLocal')]),
        );

        setRowData(
          userList.map((user) => ({
            id: user.id,
            name: `${user.extensionNumber} ${user.firstName} ${user.lastName}`,
            isAdmin: roles[0].includes(user.id),
            isSuper: roles[3].includes(user.id),
            threshold: user['Missed Queue Call Threshold']?.toString() || '',
            schedule: scheduleMap.get(user.id) || null,
          })),
        );
      } catch (err) {
        console.error('Error loading user data:', err);
      }
    })();
  }, [token]);

  const onCheckboxChange = useCallback(
    async (id: string, checked: boolean) => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/roles/${id}`, {
          method: checked ? 'PUT' : 'DELETE',
          headers: {
            authorization: `Bearer ${token}`,
            ...(checked && { 'Content-Type': 'application/json' }),
          },
          ...(checked && { body: JSON.stringify({ role: 'admin' }) }),
        });

        const rowNode = gridRef.current?.api?.getRowNode(id);
        if (rowNode && res.ok) {
          rowNode.setDataValue('isAdmin', checked);
        }
      } catch (error) {
        console.error('Error updating role:', error);
        const rowNode = gridRef.current?.api?.getRowNode(id);
        if (rowNode) {
          rowNode.setDataValue('isAdmin', !checked);
        }
      }
    },
    [token],
  );

  const onThresholdCellChange = useCallback(
    async (event: any) => {
      const { id, threshold } = event.data;

      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/queue-threshold/${id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            missedQueueCallThreshold: threshold,
          }),
        });

        if (!res.ok) {
          throw new Error(`Failed to update threshold for user ${id}`);
        }
      } catch (err) {
        console.error('Error updating threshold:', err);
        const rowNode = gridRef.current?.api?.getRowNode(id);
        if (rowNode) {
          rowNode.setDataValue('threshold', event.oldValue);
        }
      }
    },
    [token],
  );

  const onScheduleCellChange = useCallback(
    async (event: any) => {
      const { id, schedule } = event.data;
      try {
        let res;

        if (schedule === null) {
          res = await fetch(`${process.env.REACT_APP_API_URL}/logoff/schedule/${id}`, {
            method: 'DELETE',
            headers: {
              authorization: `Bearer ${token}`,
            },
          });
        } else {
          const utcSchedule = convertScheduleWithOffset(schedule, 'toUTC');
          res = await fetch(`${process.env.REACT_APP_API_URL}/logoff/schedule/${id}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ schedule: utcSchedule }),
          });
        }

        if (!res.ok) {
          throw new Error(`Failed to update schedule for user ${id}`);
        }
      } catch (err) {
        console.error('Error updating schedule:', err);
        const rowNode = gridRef.current?.api?.getRowNode(id);
        if (rowNode) {
          rowNode.setDataValue('schedule', event.oldValue);
        }
      }
    },
    [token],
  );

  const colDefs = useMemo(() => {
    const cols: any[] = [
      {
        headerName: 'Admin',
        field: 'isAdmin',
        cellRenderer: (params: any) => (
          <input
            type="checkbox"
            checked={params.value}
            onChange={(e) => onCheckboxChange(params.data.id, e.target.checked)}
            disabled={params.data.isSuper}
          />
        ),
        editable: false,
        width: 100,
        suppressSizeToFit: true,
      },
      {
        headerName: 'Name',
        field: 'name',
        editable: false,
        flex: 1,
        filter: 'agSetColumnFilter',
      },
      {
        headerName: 'Logoff Time (24h format, hh:mm)',
        field: 'schedule',
        editable: true,
        flex: 1,
        valueParser: (input: any) => {
          const raw = typeof input === 'string' ? input : input.newValue;
          if (typeof raw !== 'string') return input.oldValue;
          const trimmed = raw.trim();
          if (trimmed === '') return null;

          const match = trimmed.match(/^([01]?[0-9]|2[0-3]):([0-5][0-9])$/);
          if (!match) return input.oldValue;

          const [hh, mm] = match.slice(1).map(Number);
          return [hh, mm];
        },
        valueGetter: (params: any) => {
          const { schedule } = params.data;
          if (schedule) {
            const [hh, mm] = schedule;
            return `${hh.toString().padStart(2, '0')}:${mm.toString().padStart(2, '0')}`;
          }
          return '';
        },
      },
    ];

    if (orgId === '3154756020') {
      cols.push({
        headerName: 'Threshold',
        field: 'threshold',
        editable: true,
        flex: 1,
        cellEditor: 'agTextCellEditor',
        cellEditorParams: {
          min: 1,
          max: MAX_THRESHOLD,
          step: 5,
        },
      });
    }
    return cols;
  }, [onCheckboxChange]);

  const defaultColDef = useMemo(
    () => ({
      sortable: false,
      filter: true,
      resizable: true,
      minWidth: 50,
    }),
    [],
  );

  return (
    <div className={styles.gridContainer}>
      <div className={styles.navbar}>
        <div className={styles.tabButtons}>
          <button
            type="button"
            className={`${styles.subTabButton} ${styles.subTabButtonActive}`}
            style={{ display: orgId === '3154756020' ? '' : 'none' }}
          >
            Manage User Configurations
          </button>
        </div>
      </div>

      <div
        className={`ag-theme-alpine${!lightMode ? '-dark' : ''} ${styles.agThemeAlpineOverrides} ${styles.gridWrapper}`}
        style={{ flex: 3 }}
      >
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={colDefs}
          defaultColDef={defaultColDef}
          singleClickEdit
          stopEditingWhenCellsLoseFocus
          animateRows
          suppressCellFocus
          rowSelection="multiple"
          suppressRowClickSelection
          suppressDragLeaveHidesColumns
          headerHeight={40}
          rowHeight={40}
          getRowId={(params) => params.data.id}
          onCellValueChanged={(event) => {
            if (event.colDef.field === 'threshold') {
              onThresholdCellChange(event);
            } else if (event.colDef.field === 'schedule') {
              onScheduleCellChange(event);
            }
          }}
        />
      </div>

      <Notes
        lightMode={lightMode}
        notes={`
          This tab allows for user configurations. Superadmins may promote normal users to admins.
          Admins may manage the user thresholds of each user. A user threshold is an optional
          rule that denotes how many queue calls can be missed before the user is signed out of
          all queues. Admins may also pick a scheduled logoff, this will sign the user out of their queues
          every day at the selected hour and minutes (24h, hh:mm format).
        `}
      />
    </div>
  );
}
